/**
 * How the web names a sign-in requirement's date (docs/PLAN-signin-requirements-20261029.md, "the contract": always a
 * named date, never "in N days").
 *
 * The doors send every requirement date (`dueAtMs`, and a policy's `earliestDueAtMs` / `latestDueAtMs`) as NOON UTC of
 * the due calendar day, so the instant names the same day in every zone; the wall goes up once that day has ended
 * everywhere. A due day is therefore always written in UTC (`timeZone: 'UTC'`), never in the browser's zone, which
 * would name the day before for a reader west of UTC-12 or after for one east of UTC+12. A date picked on Portal
 * Controls is sent back the same way: noon UTC of the picked day.
 *
 * An instant that is not a day (when a help code stops working) is written in the reader's own zone.
 *
 * Pure, and imports nothing: the banner band reads it on the first download.
 */

const DAY_MS = 86_400_000;

/** A due day with its weekday, as the reader's language writes it: "Thu, Nov 12". */
export function dueDay(dueAtMs: number): string {
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(dueAtMs));
}

/** A due day without its weekday, for a pill or a phone's line: "Nov 12". */
export function dueDayShort(dueAtMs: number): string {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(dueAtMs));
}

/**
 * Whole calendar days from the reader's own today to the due day: 0 on the day itself (wherever the reader is, the day
 * they read as "Thu, Nov 12"), negative once it has passed for them. The due day is the UTC calendar day of `dueAtMs`;
 * today is the reader's local calendar day of `nowMs`.
 */
export function daysToDue(dueAtMs: number, nowMs: number): number {
  const due = new Date(dueAtMs);
  const now = new Date(nowMs);
  const dueDayNumber = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate()) / DAY_MS;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS;
  return Math.round(dueDayNumber - today);
}

/** The day a noon-UTC instant names, as a date field holds it: "2026-11-12". */
export function isoDay(dueAtMs: number): string {
  return new Date(dueAtMs).toISOString().slice(0, 10);
}

/** Noon UTC of a day a date field holds ("2026-11-12"), the instant the doors take; null for anything else. */
export function noonUtcOf(day: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(y, mo - 1, d, 12);
  const back = new Date(ms);
  // "2026-02-30" is not a day: Date.UTC would roll it into March.
  return back.getUTCFullYear() === y && back.getUTCMonth() === mo - 1 && back.getUTCDate() === d ? ms : null;
}

/** An instant's time of day in the reader's own zone: "3:42 PM". */
export function clockTime(atMs: number): string {
  return new Date(atMs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
